import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles, User, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";
import { IconOnly } from "../brand";

/**
 * AskAuriWidget — Altura Global's public AI Investment Concierge widget.
 * Public-only. Mounts from PublicLayout.
 *
 * Contextual pre-fill: any element on the page can dispatch a CustomEvent
 * `altura:ask-auri` with detail = { prompt: string, autosubmit?: boolean }.
 * The widget opens and either fills the composer or auto-submits.
 */

const SUGGESTION_CHIPS = [
  { key: "explore-ghana",     label: "Explore Ghana",           prompt: "Tell me about investing in Ghana." },
  { key: "compare-countries", label: "Compare Countries",       prompt: "Help me compare Altura's international markets." },
  { key: "process",           label: "The Investment Process",  prompt: "Walk me through Altura's investment process." },
  { key: "residency",         label: "Residency Programs",      prompt: "What do I need to know about residency programs?" },
  { key: "opportunities",     label: "Available Opportunities", prompt: "What opportunities does Altura publish right now?" },
  { key: "how-altura",        label: "How Altura Works",        prompt: "How does Altura work — what makes you different?" },
  { key: "advisory",          label: "Book an Advisory Session", prompt: "I'd like to speak with an advisor." },
  { key: "articles",          label: "Latest Articles",         prompt: "Share your latest Learning Center articles." },
];

/**
 * Lightweight, safe subset of Markdown for AURI's assistant replies.
 * Handles: **bold**, [label](href) links, blank-line paragraphs.
 * No raw HTML is ever injected — text is split by regex and rendered as
 * React nodes. Internal links (starting with /) render as SPAs; external
 * links open in a new tab.
 */
function AssistantMarkdown({ content }) {
  const paragraphs = String(content || "").split(/\n{2,}/);
  const linkRe = /(\[([^\]]+)\]\(([^)]+)\))/g;
  const boldRe = /(\*\*([^*]+)\*\*)/g;

  const renderInline = (text, kp) => {
    const nodes = [];
    let idx = 0;
    // First pass: extract links
    const parts = [];
    let last = 0;
    for (const m of text.matchAll(linkRe)) {
      if (m.index > last) parts.push({ t: "text", v: text.slice(last, m.index) });
      parts.push({ t: "link", label: m[2], href: m[3] });
      last = m.index + m[1].length;
    }
    if (last < text.length) parts.push({ t: "text", v: text.slice(last) });

    // Second pass: render bold on each text part
    for (const p of parts) {
      idx++;
      if (p.t === "link") {
        const isInternal = p.href.startsWith("/");
        nodes.push(
          <a
            key={`${kp}-l-${idx}`}
            href={p.href}
            {...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" })}
            className="text-altura-gold-dark underline decoration-altura-gold/40 underline-offset-2 hover:decoration-altura-gold"
          >{p.label}</a>
        );
      } else {
        // bold segmentation
        let lastB = 0;
        let bIdx = 0;
        for (const bm of p.v.matchAll(boldRe)) {
          if (bm.index > lastB) nodes.push(<span key={`${kp}-t-${idx}-${bIdx++}`}>{p.v.slice(lastB, bm.index)}</span>);
          nodes.push(<strong key={`${kp}-b-${idx}-${bIdx++}`} className="text-altura-charcoal">{bm[2]}</strong>);
          lastB = bm.index + bm[1].length;
        }
        if (lastB < p.v.length) nodes.push(<span key={`${kp}-t-${idx}-${bIdx++}`}>{p.v.slice(lastB)}</span>);
      }
    }
    return nodes;
  };

  return (
    <div className="space-y-2 whitespace-pre-wrap">
      {paragraphs.map((para, i) => (
        <p key={i} className="m-0 leading-relaxed">{renderInline(para, `p${i}`)}</p>
      ))}
    </div>
  );
}

function randomId() {
  return "aur-" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-4);
}

export default function AskAuriWidget() {
  const [open, setOpen] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [welcome, setWelcome] = useState("");
  const [sessionId] = useState(() => {
    const cached = typeof window !== "undefined" && sessionStorage.getItem("auri_session");
    if (cached) return cached;
    const id = randomId();
    if (typeof window !== "undefined") sessionStorage.setItem("auri_session", id);
    return id;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [convoId, setConvoId] = useState(null);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "" });
  const scrollRef = useRef(null);

  // Load config on mount
  useEffect(() => {
    api.get("/public/auri/config")
      .then(r => { setEnabled(!!r.data.is_enabled); setWelcome(r.data.welcome_message || ""); })
      .catch(() => setEnabled(false));
  }, []);

  // Contextual "Ask AURI About This" — listen for a global CustomEvent
  useEffect(() => {
    const handler = (ev) => {
      const detail = ev.detail || {};
      setOpen(true);
      if (detail.prompt) {
        if (detail.autosubmit) {
          // Small delay so the panel is mounted before send() runs
          setTimeout(() => send(detail.prompt), 200);
        } else {
          setInput(detail.prompt);
        }
      }
    };
    window.addEventListener("altura:ask-auri", handler);
    return () => window.removeEventListener("altura:ask-auri", handler);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async (text) => {
    if (!text || !text.trim() || sending) return;
    setSending(true);
    const userMsg = { role: "user", content: text, id: randomId() };
    setMessages(m => [...m, userMsg]);
    setInput("");
    try {
      const { data } = await api.post("/public/auri/chat", {
        session_id: sessionId,
        message: text,
        source_page: typeof window !== "undefined" ? window.location.pathname : "/",
        visitor_name: contact.name || null,
        visitor_email: contact.email || null,
      });
      setConvoId(data.conversation_id);
      setMessages(m => [...m, { role: "assistant", content: data.reply, id: randomId(), handoff: data.handoff_created }]);
    } catch (err) {
      const msg = err?.response?.data?.detail || "I couldn't reach the Altura servers — please try again in a moment.";
      setMessages(m => [...m, { role: "assistant", content: msg, id: randomId(), error: true }]);
    } finally {
      setSending(false);
    }
  };

  const submitHandoff = async () => {
    if (!contact.email || !contact.email.includes("@")) return;
    try {
      await api.post("/public/auri/handoff", {
        conversation_id: convoId,
        reason: "Visitor requested to speak with the Altura team.",
        visitor_name: contact.name || null,
        visitor_email: contact.email,
      });
      setMessages(m => [...m, { role: "assistant", content: `Thank you${contact.name ? ", " + contact.name : ""}. I've notified the Altura Global team — an advisor will reach out to you at ${contact.email} shortly.`, id: randomId() }]);
      setHandoffOpen(false);
    } catch (err) {
      setMessages(m => [...m, { role: "assistant", content: "I couldn't submit that just now — please try again in a moment.", id: randomId(), error: true }]);
    }
  };

  if (!enabled) return null;

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-40 bottom-6 right-24 sm:bottom-8 sm:right-28 group"
          data-testid="askauri-open"
          aria-label="Open Ask AURI"
        >
          <span className="inline-flex items-center gap-2 rounded-full pl-3 pr-4 py-2.5 bg-altura-espresso text-white shadow-[0_10px_30px_-8px_rgba(20,20,25,0.35)] hover:shadow-[0_16px_40px_-10px_rgba(179,143,70,0.5)] transition-all hover:-translate-y-0.5">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-altura-gold text-altura-espresso">
              <Sparkles className="h-3 w-3" />
            </span>
            <span className="text-sm font-medium tracking-wide">Ask AURI</span>
          </span>
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed z-50 bottom-4 right-4 sm:bottom-8 sm:right-8 w-[calc(100vw-2rem)] sm:w-[420px] max-w-[95vw] max-h-[85vh] flex flex-col rounded-2xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(20,20,25,0.35)] border border-altura-gold/40 bg-white" data-testid="askauri-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-altura-gray-line bg-white">
            <div className="flex items-center gap-2.5">
              <div className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-altura-espresso text-altura-gold shadow-inner">
                <IconOnly size={22} theme="on-dark" />
              </div>
              <div>
                <div className="text-sm font-editorial text-altura-charcoal leading-none">AURI</div>
                <div className="text-[10px] tracking-[0.16em] uppercase text-altura-gold-dark">Altura Investment Concierge</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-altura-charcoal/50 hover:text-altura-charcoal" aria-label="Close" data-testid="askauri-close">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Switch to Voice affordance */}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setTimeout(() => window.dispatchEvent(new CustomEvent("altura:talk-to-auri")), 60);
            }}
            className="flex items-center justify-between gap-2 px-5 py-2.5 border-b border-altura-gray-line bg-altura-ivory-2 hover:bg-altura-gold/10 transition-colors text-left"
            data-testid="askauri-switch-voice"
            aria-label="Switch to Voice AURI"
          >
            <span className="inline-flex items-center gap-2 text-[12px] tracking-wide text-altura-espresso">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-altura-gold animate-pulse" aria-hidden="true" />
              Prefer to talk? Try <strong className="font-semibold">Voice AURI</strong>
            </span>
            <span className="text-[11px] uppercase tracking-[0.14em] text-altura-gold-dark">Open →</span>
          </button>

          {/* Message area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4 bg-altura-ivory-2">
            {messages.length === 0 && (
              <>
                <div className="rounded-lg bg-white border border-altura-gray-line p-4 text-sm text-altura-charcoal/85 leading-relaxed">
                  {welcome || "Hello, I'm AURI, Altura Global's AI Investment Concierge."}
                </div>
                <div>
                  <div className="eyebrow text-altura-charcoal/50 mb-2">Try asking about</div>
                  <div className="flex flex-wrap gap-2">
                    {SUGGESTION_CHIPS.map(chip => (
                      <button key={chip.key}
                              onClick={() => send(chip.prompt)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs text-altura-charcoal border border-altura-gold/40 bg-white hover:bg-altura-gold/10 hover:border-altura-gold transition-colors"
                              data-testid={`askauri-chip-${chip.key}`}>
                        {chip.label} <ChevronRight className="h-3 w-3" />
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
            {messages.map(m => (
              <div key={m.id} className={m.role === "user" ? "flex justify-end" : ""}>
                <div className={`max-w-[86%] rounded-lg p-3.5 text-sm leading-relaxed ${m.role === "user" ? "bg-altura-espresso text-white" : m.error ? "bg-white border border-altura-burgundy/30 text-altura-charcoal" : "bg-white border border-altura-gray-line text-altura-charcoal"}`}>
                  {m.role === "assistant" && !m.error
                    ? <AssistantMarkdown content={m.content} />
                    : <div className="whitespace-pre-wrap">{m.content}</div>}
                  {m.handoff && <div className="mt-2 text-[11px] text-altura-gold-dark">✓ The Altura team has been notified.</div>}
                </div>
              </div>
            ))}
            {sending && (
              <div className="max-w-[86%] rounded-lg p-3.5 bg-white border border-altura-gray-line inline-flex items-center gap-2 text-sm text-altura-charcoal/50">
                <span className="inline-block h-2 w-2 rounded-full bg-altura-gold animate-pulse" />
                <span>AURI is composing…</span>
              </div>
            )}
          </div>

          {/* Handoff panel */}
          {handoffOpen && (
            <div className="p-4 border-t border-altura-gray-line bg-white space-y-3">
              <div className="text-sm text-altura-charcoal/80">Share your name and email — an Altura advisor will reach out to you personally.</div>
              <input value={contact.name} onChange={e => setContact({...contact, name: e.target.value})} placeholder="Your name" className="w-full px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="askauri-name" />
              <input type="email" value={contact.email} onChange={e => setContact({...contact, email: e.target.value})} placeholder="Your email" className="w-full px-3 py-2 border border-altura-gray-line rounded-md text-sm" data-testid="askauri-email" />
              <div className="flex justify-end gap-2">
                <button onClick={() => setHandoffOpen(false)} className="text-xs px-3 py-2 text-altura-charcoal/60 hover:text-altura-charcoal">Cancel</button>
                <button onClick={submitHandoff} disabled={!contact.email || !contact.email.includes("@")} className="btn-gold text-xs px-4 py-2 disabled:opacity-50" data-testid="askauri-handoff-submit">Notify Altura team</button>
              </div>
            </div>
          )}

          {/* Composer */}
          {!handoffOpen && (
            <>
              <div className="px-4 pt-2 pb-1 bg-white flex items-center justify-between">
                <button onClick={() => setHandoffOpen(true)} className="text-[11px] tracking-wide text-altura-gold-dark hover:text-altura-espresso inline-flex items-center gap-1" data-testid="askauri-handoff-cta">
                  <User className="h-3 w-3" /> Speak with an Altura advisor
                </button>
                <div className="text-[10px] text-altura-charcoal/40">Session {sessionId.slice(-6)}</div>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(input); }} className="flex gap-2 p-4 border-t border-altura-gray-line bg-white">
                <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask AURI about markets, opportunities, resources…"
                       className="flex-1 px-3 py-2 border border-altura-gray-line rounded-md text-sm focus:outline-none focus:border-altura-gold" data-testid="askauri-input" />
                <button type="submit" disabled={sending || !input.trim()} className="btn-gold px-4 disabled:opacity-50" data-testid="askauri-send">
                  <Send className="h-4 w-4" />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
}
